/* eslint-disable react-hooks/exhaustive-deps */
import { Box, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import Select from '../ui/forms/fields/select';
import TeamColorPicker from '../ui/teamControls/teamColorPicker';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';

const EventMultipleVs = ({ selectedTeamA, selectedTeamB, section }) => {
  const eventTeams = useSelector((state) => state.event.event.teams);
  const eventTeamGroups = useSelector((state) => state.event.event.teamGroups || []);
  const initialDraw = section?.initialDraw;

  const [teamA, setTeamA] = useState({
    players: [],
    color: 'green',
    name: null,
    teamGroupId: null
  });
  const [teamB, setTeamB] = useState({ players: [], color: 'red', name: null, teamGroupId: null });
  const [teams, setTeams] = useState({ teamA, teamB });
  const [teamAGroupColor, setTeamAGroupColor] = useState(null);
  const [teamBGroupColor, setTeamBGroupColor] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    register,
    setValue,
    watch,
    control,
    formState: { errors }
  } = useFormContext();

  useEffect(() => {
    register('teams');
  }, [register]);

  useEffect(() => {
    setTeams({ teamA, teamB });
  }, [teamA, teamB]);

  useEffect(() => {
    setValue('teams', teams);
  }, [teams, setValue]);

  const watchTeamAColor = watch('teamAColor');
  const watchTeamBColor = watch('teamBColor');

  useEffect(() => {
    setTeamA((prev) => ({ ...prev, color: teamAGroupColor || watchTeamAColor || 'red' }));
  }, [watchTeamAColor, teamAGroupColor]);

  useEffect(() => {
    setTeamB((prev) => ({ ...prev, color: teamBGroupColor || watchTeamBColor || 'green' }));
  }, [watchTeamBColor, teamBGroupColor]);

  const watchTeamA = watch('teamA');
  const watchTeamB = watch('teamB');

  useEffect(() => {
    if (watchTeamA) {
      onTeamChange(watchTeamA, 'teamA');
    }
  }, [watchTeamA]);

  useEffect(() => {
    if (watchTeamB) {
      onTeamChange(watchTeamB, 'teamB');
    }
  }, [watchTeamB]);

  const getTeamGroupColor = (selectedTeam) => {
    if (!selectedTeam?.groupId) {
      return null;
    }

    const group = eventTeamGroups.find((g) => g.id === selectedTeam.groupId);
    return group?.color || null;
  };

  const onTeamChange = (value, team) => {
    // Find team in eventTeams
    const selectedTeam = eventTeams.find((teamData) => teamData.id === value);
    if (!selectedTeam) {
      return;
    }

    // Convert members to array of name + surname strings
    const players = (selectedTeam.members || []).map(
      (member) => `${member.name} ${member.surname}`
    );
    const groupColor = getTeamGroupColor(selectedTeam);

    if (team === 'teamA') {
      const nextColor = groupColor || watchTeamAColor || 'green';
      setTeamAGroupColor(groupColor);
      setValue('teamAColor', nextColor);
      // Set teamA
      setTeamA((prev) => ({
        ...prev,
        players,
        id: selectedTeam.id,
        name: selectedTeam.name,
        teamGroupId: selectedTeam.groupId || null,
        color: nextColor
      }));
    } else {
      const nextColor = groupColor || watchTeamBColor || 'red';
      setTeamBGroupColor(groupColor);
      setValue('teamBColor', nextColor);
      // Set teamB
      setTeamB((prev) => ({
        ...prev,
        players,
        id: selectedTeam.id,
        name: selectedTeam.name,
        teamGroupId: selectedTeam.groupId || null,
        color: nextColor
      }));
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={!isMobile ? 'row' : 'column'}
      justifyContent={'space-evenly'}
      alignItems={'center'}
      gap={1}>
      <Box display="flex" flexDirection={'row'} alignItems={'center'} width={'100%'} gap={1}>
        <Select
          name="teamA"
          control={control}
          error={errors.teamA}
          defaultValue={selectedTeamA ? selectedTeamA : null}>
          {initialDraw
            ? initialDraw.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.position}. {team.name}
                </MenuItem>
              ))
            : eventTeams &&
              [...eventTeams]
                .sort((a, b) => a.name.localeCompare(b.name)) // Sort the teams by name
                .map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
        </Select>
        {teamAGroupColor ? (
          <Box
            sx={{
              width: 25,
              minWidth: 25,
              height: 25,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: teamAGroupColor
            }}
          />
        ) : (
          <TeamColorPicker name={'teamAColor'} defaultValue={'red'} />
        )}
      </Box>
      <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mx: 2 }}>
        VS
      </Typography>
      <Box display="flex" flexDirection={'row'} alignItems={'center'} width={'100%'} gap={1}>
        <Select
          name="teamB"
          control={control}
          error={errors.teamB}
          defaultValue={selectedTeamB ? selectedTeamB : null}>
          {initialDraw
            ? initialDraw.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.position}. {team.name}
                </MenuItem>
              ))
            : eventTeams &&
              [...eventTeams]
                .sort((a, b) => a.name.localeCompare(b.name)) // Sort the teams by name
                .map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
        </Select>
        {teamBGroupColor ? (
          <Box
            sx={{
              width: 25,
              minWidth: 25,
              height: 25,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: teamBGroupColor
            }}
          />
        ) : (
          <TeamColorPicker name={'teamBColor'} defaultValue={'green'} />
        )}
      </Box>
    </Box>
  );
};

export default EventMultipleVs;
